import { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { User } from "@supabase/supabase-js";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (id: string) => void;
  user: User | null;
  onAuthClick: () => void;
  onDashboardClick: () => void;
}

const navItems = [
  { label: "Prozess", id: "process" },
  { label: "Leistungen", id: "features" },
  { label: "Ergebnisse", id: "outcomes" },
  { label: "FAQ", id: "faq" },
];

export const MobileMenu = ({ 
  isOpen, 
  onClose, 
  onNavigate, 
  user, 
  onAuthClick, 
  onDashboardClick 
}: MobileMenuProps) => {
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNavClick = (id: string) => {
    onNavigate(id);
    onClose();
  };

  const handleAuthClick = () => {
    onAuthClick();
    onClose();
  };

  const handleDashboardClick = () => {
    onDashboardClick();
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Slide-in Menu */}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-background border-l border-border z-50 transform transition-transform duration-300 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <span className="text-lg font-semibold text-foreground">Menu</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted/50 transition-colors active:scale-95"
              aria-label="Menü schließen"
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 py-8 px-6">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className="w-full text-left py-4 px-4 text-lg font-medium text-foreground hover:bg-muted/30 active:bg-muted/50 rounded-lg transition-colors"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* CTA Buttons */}
          <div className="p-6 border-t border-border space-y-3">
            {user ? (
              <Button
                className="w-full h-12 font-semibold"
                onClick={handleDashboardClick}
              >
                Zum Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="w-full h-12 font-semibold"
                  onClick={handleAuthClick}
                >
                  Anmelden
                </Button>
                <Button
                  className="w-full h-12 font-semibold"
                  onClick={() => window.location.href = 'mailto:info@quentari.com?subject=Pilot%20Commitment'}
                >
                  Pilot starten
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
